import type { TemplatePortfolioDraft, TemplatePortfolioExecutionItem } from "@adpilot/shared";
import {
  IconArrowRight,
  IconCheck,
  IconCircle,
  IconCircleCheck,
  IconCircleX,
  IconLock,
  IconPlayerPause,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { getApiErrorCode, getApiErrorMessage } from "@/lib/api-error";
import {
  templatePortfolioKeys,
  useRetryTemplatePortfolioExecutionItem,
  useTemplatePortfolioExecution,
} from "./api";
import {
  ButtonSpinner,
  ExecutionBadge,
  ExecutionList,
  ExecutionProgressCard,
  ExecutionRow,
  failureReason,
  pinFirst,
  RowRetryButton,
  RowSpinner,
} from "./execution/execution-ui";
import { TemplatePortfolioFooter } from "./TemplatePortfolioShell";

export interface StepExecuteProps {
  shopId: string;
  draft: TemplatePortfolioDraft;
  executionId: string;
  /** Moves to ⑤ 开启 once creation has finished (completed or partial). No way back: N5.8. */
  onNext: () => void;
}

/** ④ 创建 (docs/tech/12 §3 ④, Paper TP-10 创建中 / TP-11 创建完成 / TP-12 部分失败). */
export function StepExecute({ shopId, draft, executionId, onNext }: StepExecuteProps) {
  const { t } = useTranslation("templatePortfolio");
  const navigate = useNavigate({ from: "/campaigns/template-portfolio" });
  const queryClient = useQueryClient();
  const query = useTemplatePortfolioExecution(shopId, executionId);
  const retry = useRetryTemplatePortfolioExecutionItem(shopId, executionId);
  const [retryingAll, setRetryingAll] = useState(false);

  if (query.isPending) return <Skeleton className="h-[560px] w-full rounded-lg" />;
  if (query.isError) {
    return (
      <ErrorState
        message={t("execute.loadError")}
        onRetry={() => void query.refetch()}
        isRetrying={query.isFetching}
      />
    );
  }

  const execution = query.data;
  const { counts } = execution;
  const running = execution.status === "pending" || execution.status === "running";
  const processed = counts.succeeded + counts.failed + counts.skipped;
  const portfolioCreated = execution.portfolio.amazonPortfolioId !== null;
  const failedItems = execution.items.filter((item) => item.status === "failed");
  // The portfolio itself could not be created: nothing exists on Amazon, so the user
  // goes back to the preview to fix the name and run again.
  const stoppedEarly = execution.status === "failed" && !portfolioCreated;

  async function retryItems(items: TemplatePortfolioExecutionItem[]) {
    for (const item of items) {
      try {
        await retry.mutateAsync(item.id);
      } catch (error) {
        const code = await getApiErrorCode(error);
        toast.error(
          code === "template_portfolio.execution.item_not_retryable"
            ? t("execute.notRetryable", { name: item.campaignName ?? "" })
            : await getApiErrorMessage(error),
        );
      }
    }
  }

  async function retryAll() {
    setRetryingAll(true);
    try {
      await retryItems(failedItems);
    } finally {
      setRetryingAll(false);
    }
  }

  const caption = running
    ? execution.portfolio.name
    : `${execution.portfolio.name} · ${t(
        execution.status === "completed"
          ? "execute.captionCompleted"
          : stoppedEarly
            ? "execute.captionStopped"
            : "execute.captionPartial",
      )}`;

  const unit = running
    ? t("execute.unitProcessed")
    : counts.failed > 0
      ? t("execute.unitCreatedWithFailures", { count: counts.failed })
      : t("execute.unitCreated");

  const legend = [
    { tone: "success" as const, count: counts.succeeded, label: t("execute.legend.succeeded") },
    { tone: "destructive" as const, count: counts.failed, label: t("execute.legend.failed") },
    { tone: "brand" as const, count: counts.running, label: t("execute.legend.running") },
    { tone: "track" as const, count: counts.pending, label: t("execute.legend.pending") },
    { tone: "track" as const, count: counts.skipped, label: t("execute.legend.skipped") },
  ].filter((segment) => segment.count > 0);

  const portfolioLink = execution.portfolio.portfolioId ? (
    <Button
      variant="outline"
      className="bg-surface text-xs"
      render={<Link to="/portfolios/$id" params={{ id: execution.portfolio.portfolioId }} />}
    >
      {t("execute.toPortfolio")}
    </Button>
  ) : null;

  const pausedHint = (text: string) => (
    <span className="flex items-center gap-1.5 text-xs">
      <IconPlayerPause className="size-3.5 shrink-0" aria-hidden />
      {text}
    </span>
  );

  return (
    <>
      <ExecutionProgressCard
        variant="execute"
        caption={caption}
        value={`${running ? processed : counts.succeeded} / ${counts.total}`}
        unit={unit}
        legend={legend}
        bar={legend}
      >
        {portfolioCreated ? (
          <span className="inline-flex h-6 items-center gap-1.5 rounded-[6px] bg-badge-success-background px-2 font-medium text-success-foreground text-xs">
            <IconCheck className="size-3" stroke={2.4} aria-hidden />
            {t("execute.portfolioCreated")}
          </span>
        ) : null}
        {!running && (portfolioCreated || stoppedEarly) ? (
          <>
            {portfolioCreated ? <span className="h-px w-4 shrink-0 bg-line" /> : null}
            <span className="text-text-secondary text-xs">
              {stoppedEarly
                ? (failureReason(execution.errorCode) ?? t("execute.stoppedHint"))
                : counts.failed > 0
                  ? t("execute.partialNote")
                  : t("execute.completedNote")}
            </span>
          </>
        ) : null}
      </ExecutionProgressCard>

      <ExecutionList variant="execute" detailHeader={t("execute.columns.progress")}>
        {pinFirst(
          execution.items,
          (item) => item.status === "failed" || item.status === "skipped",
        ).map((item) => (
          <ExecuteRow
            key={item.id}
            item={item}
            retrying={retry.isPending && retry.variables === item.id}
            disabled={retryingAll}
            onRetry={() => void retryItems([item])}
          />
        ))}
      </ExecutionList>

      {running ? (
        <TemplatePortfolioFooter
          hint={
            <span className="flex items-center gap-1.5 text-xs">
              <IconLock className="size-3.5 shrink-0" aria-hidden />
              {t("execute.runningHint")}
            </span>
          }
        >
          <Button disabled className="text-xs disabled:opacity-60">
            <ButtonSpinner />
            {t("execute.creatingButton", {
              current: Math.min(processed + 1, counts.total),
              total: counts.total,
            })}
          </Button>
        </TemplatePortfolioFooter>
      ) : stoppedEarly ? (
        <TemplatePortfolioFooter>
          <Button
            className="text-xs"
            onClick={() => {
              // The failed run reopened the draft with a new version; ③ must not keep the old one.
              void queryClient.invalidateQueries({
                queryKey: templatePortfolioKeys.draft(draft.id),
              });
              void navigate({ search: (prev) => ({ ...prev, step: 3, executionId: undefined }) });
            }}
          >
            {t("execute.backToPreview")}
          </Button>
        </TemplatePortfolioFooter>
      ) : (
        <TemplatePortfolioFooter
          hint={pausedHint(
            counts.failed > 0 ? t("execute.partialHint") : t("execute.completedHint"),
          )}
        >
          {failedItems.length > 0 ? (
            <Button
              variant="destructive-outline"
              className="border-badge-destructive-border bg-surface text-xs"
              loading={retryingAll}
              disabled={retry.isPending}
              onClick={() => void retryAll()}
            >
              {t("execute.retryAll", { count: failedItems.length })}
            </Button>
          ) : null}
          {portfolioLink}
          {/* Enabling needs at least one created campaign (the API refuses otherwise). */}
          <Button className="text-xs" disabled={counts.succeeded === 0} onClick={onNext}>
            {t("execute.next")}
            <IconArrowRight className="size-3.5 opacity-100" aria-hidden />
          </Button>
        </TemplatePortfolioFooter>
      )}
    </>
  );
}

function ExecuteRow({
  item,
  retrying,
  disabled,
  onRetry,
}: {
  item: TemplatePortfolioExecutionItem;
  retrying: boolean;
  disabled: boolean;
  onRetry: () => void;
}) {
  const { t } = useTranslation("templatePortfolio");
  const name = item.campaignName ?? t(`enable.layers.${item.layer}`);
  switch (item.status) {
    case "succeeded":
      return (
        <ExecutionRow
          variant="execute"
          icon={<IconCircleCheck className="text-success-foreground" stroke={1.8} aria-hidden />}
          name={name}
          detail={t("execute.detail.succeeded")}
          status={
            <ExecutionBadge variant="execute" tone="success">
              {t("execute.status.succeededPaused")}
            </ExecutionBadge>
          }
        />
      );
    case "failed": {
      const reason = failureReason(item.errorCode);
      return (
        <ExecutionRow
          variant="execute"
          className="bg-badge-destructive-background/50"
          icon={<IconCircleX className="text-destructive-foreground" stroke={1.8} aria-hidden />}
          name={name}
          detail={
            <span className="line-clamp-2" title={item.errorMessage ?? undefined}>
              {item.errorMessage
                ? reason
                  ? t("execute.detail.failedWithReasonAndMessage", {
                      reason,
                      message: item.errorMessage,
                    })
                  : t("execute.detail.failedWithMessage", { message: item.errorMessage })
                : reason
                  ? t("execute.detail.failedWithReason", { reason })
                  : t("execute.detail.failed")}
            </span>
          }
          detailClassName="text-destructive-foreground"
          status={<RowRetryButton onClick={onRetry} disabled={disabled || retrying} />}
        />
      );
    }
    case "running":
      return (
        <ExecutionRow
          variant="execute"
          icon={<RowSpinner />}
          name={name}
          detail={t("execute.detail.running")}
          detailClassName="text-text-secondary"
          status={
            <ExecutionBadge variant="execute" tone="info">
              {t("execute.status.running")}
            </ExecutionBadge>
          }
        />
      );
    case "skipped":
      return (
        <ExecutionRow
          variant="execute"
          icon={<IconCircle className="text-placeholder" stroke={1.8} aria-hidden />}
          name={name}
          muted
          detail={item.skipReason ? t(`execute.skipReasons.${item.skipReason}`) : "—"}
          status={
            <ExecutionBadge variant="execute" tone="neutral">
              {t("execute.status.skipped")}
            </ExecutionBadge>
          }
        />
      );
    default:
      return (
        <ExecutionRow
          variant="execute"
          icon={<IconCircle className="text-placeholder" stroke={1.8} aria-hidden />}
          name={name}
          muted
          detail="—"
          status={
            <ExecutionBadge variant="execute" tone="neutral">
              {t("execute.status.pending")}
            </ExecutionBadge>
          }
        />
      );
  }
}
